import React, {useEffect, useState} from 'react';
import {Button, List, Popover, Progress} from 'antd';
import {useTranslation} from 'react-i18next';
import api from './api';
import useCurrentOrg from './helpers/useCurrentOrg';
import useCurrentUser from './helpers/useCurrentUser';
import {useBulkJobsDispatch, useBulkJobsState} from '../contexts/BulkJobsContext';
import {bulkJobIsActive} from './helpers/removalOutcome';
import BulkJobProgress from './explore/BulkJobProgress';

// Preserve the existing live bulk-operation surface while historical Activity
// is release-gated. This endpoint returns processing jobs, not upload history.
export default function CurrentBulkJobIndicator() {
    const org = useCurrentOrg();
    const user = useCurrentUser();
    const {bulkJobs} = useBulkJobsState();
    const bulkJobDispatch = useBulkJobsDispatch();
    const {t} = useTranslation();
    const scope = `${org?.id}:${user?.id}`;
    const [snapshot, setSnapshot] = useState({scope: null, jobs: []});
    useEffect(() => {
        let disposed = false;
        if (org && user) api.get('/api/bulk_jobs/processing').then(({data}) => {
            if (!disposed) {
                setSnapshot({scope, jobs: data});
                // Resume observation after navigation/reload even if the
                // progress popover has never been opened.
                data.forEach(job => bulkJobDispatch({type: 'add', bulkJob: job}));
            }
        }).catch(error => console.warn('Current bulk jobs unavailable:', error));
        return () => {disposed = true;};
    }, [scope, bulkJobDispatch]);
    const jobs = new Map((snapshot.scope === scope ? snapshot.jobs : []).map(job => [job.guid, job]));
    Object.values(bulkJobs).forEach(job => {
        if (String(job.organization_id) === String(org?.id) || jobs.has(job.guid)) {
            jobs.set(job.guid, {...jobs.get(job.guid), ...job});
        }
    });
    const active = [...jobs.values()].filter(bulkJobIsActive);
    if (!active.length) return null;
    const percent = active.reduce((sum, job) => sum + (job.indexing_pending && job.done ? 100 : job.progress || 0), 0) / active.length;
    return <Popover title={t('current-bulk-jobs', 'Current bulk jobs')} content={
        <List dataSource={active} renderItem={job => <List.Item>{job.action || job.summary}<BulkJobProgress id={job.guid}/></List.Item>}/>
    }><Button type="text" aria-label={t('current-bulk-jobs', 'Current bulk jobs')}>
        <Progress type="circle" percent={percent} width={40} status="active"/>
    </Button></Popover>;
}
